// month-calendar.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { runtimeLogger } from '../../../utils/runtime-logger.util';
import { Router } from '@angular/router'; // Adicionado
import { Subscription } from 'rxjs';
import { AppointmentsService } from '../../../services/appointments.service';
import {
  AppointmentMesResponse,
  AgendamentoMesDia
} from '../../../interfaces/appointment.interface';
import { LocalStorageUtil } from '../../../utils/local-storage.util';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  appointments: Appointment[];
  displayAppointments: Appointment[];
  hasMoreAppointments: boolean;
  totalAppointments: number;
  formattedDate: string;
}

interface Appointment {
  time: string;
  clientName: string;
  leadId: number;
  campaign?: string;
  fullDate?: string;
  fullName?: string;
  vendedor?: string;
}

@Component({
  selector: 'app-month-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './month-calendar.html',
  styleUrls: ['./month-calendar.scss']
})
export class MonthCalendar implements OnInit, OnDestroy {
  currentDate: Date = new Date();
  calendarDays: CalendarDay[] = [];
  weekDays: string[] = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  currentMonthYear: string = '';

  selectedDay: CalendarDay | null = null;
  isLoading: boolean = false;
  error: boolean = false;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private appointmentsService: AppointmentsService,
    private router: Router // Adicionado
  ) {}

  ngOnInit(): void {
    this.generateCalendar();
    this.loadAppointments();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  generateCalendar(): void {
    this.calendarDays = [];

    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    // Atualizar título do mês/ano
    this.currentMonthYear = this.currentDate.toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric'
    }).replace(/^\w/, c => c.toUpperCase());

    // Primeiro dia do mês
    const firstDay = new Date(year, month, 1);
    // Último dia do mês
    const lastDay = new Date(year, month + 1, 0);

    // Dia da semana do primeiro dia (0 = Domingo, 1 = Segunda, etc.)
    const firstDayIndex = firstDay.getDay();

    // Dia de hoje para comparação
    const today = new Date();
    const isToday = (date: Date) =>
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    // Adicionar dias do mês anterior (se necessário)
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      this.calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: isToday(date),
        appointments: [],
        displayAppointments: [],
        hasMoreAppointments: false,
        totalAppointments: 0,
        formattedDate: this.formatDate(date)
      });
    }

    // Adicionar dias do mês atual
    const daysInMonth = lastDay.getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      this.calendarDays.push({
        date,
        isCurrentMonth: true,
        isToday: isToday(date),
        appointments: [],
        displayAppointments: [],
        hasMoreAppointments: false,
        totalAppointments: 0,
        formattedDate: this.formatDate(date)
      });
    }

    // Adicionar dias do próximo mês (se necessário)
    const totalCells = 42; // 6 semanas * 7 dias
    const nextMonthDays = totalCells - this.calendarDays.length;
    for (let day = 1; day <= nextMonthDays; day++) {
      const date = new Date(year, month + 1, day);
      this.calendarDays.push({
        date,
        isCurrentMonth: false,
        isToday: isToday(date),
        appointments: [],
        displayAppointments: [],
        hasMoreAppointments: false,
        totalAppointments: 0,
        formattedDate: this.formatDate(date)
      });
    }
  }

  loadAppointments(): void {
    this.isLoading = true;
    this.error = false;

    // Obter o número do mês (1-12) e ano do currentDate
    const monthNumber = this.currentDate.getMonth() + 1;
    const yearNumber = this.currentDate.getFullYear();

    const sub = this.appointmentsService.getAppointmentsMes(LocalStorageUtil.getSelectedVWDn(), monthNumber, yearNumber)
      .subscribe({
        next: (response: AppointmentMesResponse) => {
          this.processAppointments(response);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar agendamentos:', error);
          this.error = true;
          this.isLoading = false;
        }
      });

    this.subscriptions.add(sub);
  }

  processAppointments(response: AppointmentMesResponse): void {
    if (!response?.agendamentosPorDia) return;

    Object.keys(response.agendamentosPorDia).forEach(dateKey => {
      const diaAgendamentos = response.agendamentosPorDia[dateKey];

      if (!diaAgendamentos || !diaAgendamentos.agendamentos) return;

      // Converter a data de "dd/MM/yyyy" para objeto Date
      const [day, month, year] = dateKey.split('/').map(Number);
      const calendarDate = new Date(year, month - 1, day);

      // Encontrar o dia correspondente no calendário
      const calendarDay = this.calendarDays.find(day =>
        day.date.getDate() === calendarDate.getDate() &&
        day.date.getMonth() === calendarDate.getMonth() &&
        day.date.getFullYear() === calendarDate.getFullYear()
      );

      if (calendarDay && diaAgendamentos.agendamentos.length > 0) {
        // Converter agendamentos para o formato esperado
        const processedAppointments: Appointment[] = diaAgendamentos.agendamentos.map((appt: AgendamentoMesDia) => ({
          time: this.formatTime(appt.horarioAgendamento),
          clientName: this.truncateName(appt.nomeCliente, 12),
          fullName: appt.nomeCliente,
          leadId: appt.idLead,
          campaign: appt.siglaTipoCampanha,
          vendedor: appt.nomeVendedor || 'Não informado',
          fullDate: dateKey
        }));

        // Ordenar por horário
        const sortedAppointments = processedAppointments.sort((a, b) => {
          return a.time.localeCompare(b.time);
        });

        calendarDay.appointments = sortedAppointments;
        calendarDay.totalAppointments = sortedAppointments.length;

        // Mostrar 4 agendamentos no calendário
        calendarDay.displayAppointments = sortedAppointments.slice(0, 4);
        calendarDay.hasMoreAppointments = sortedAppointments.length > 4;
      }
    });
  }

  formatTime(timeString: string): string {
    // A API retorna "09:00" - garantir formato correto
    if (!timeString) return '';

    // Se já estiver no formato HH:MM, retornar como está
    if (/^\d{1,2}:\d{2}(?::\d{2})?$/.test(timeString)) {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    }

    // Se for um timestamp ISO, converter
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timeString;
    }
  }

  getFormattedDateExtenso(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    return date.toLocaleDateString('pt-BR', options);
  }

  truncateName(name: string, maxLength: number = 12): string {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  }

  getAppointmentColor(index: number): string {
    // Alterna entre as duas cores solicitadas
    return index % 2 === 0 ? '#001E50' : '#4CC7F4';
  }

  // ===== NOVO MÉTODO ADICIONADO =====
  // Método para navegar para detalhes do lead
  navigateToLeadDetails(leadId: number): void {
    this.closeModal(); // Fecha o modal

    if (leadId) {
      this.router.navigate(['/leads', leadId, 'detalhes']);
    } else {
      runtimeLogger.warn('LeadId não encontrado');
      // Opcional: mostrar uma mensagem para o usuário
      alert('Não foi possível encontrar o ID do lead');
    }
  }

  openDayDetails(day: CalendarDay): void {
    this.selectedDay = day;
  }

  closeModal(): void {
    this.selectedDay = null;
  }

  previousMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.generateCalendar();
    this.loadAppointments();
  }

  nextMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.generateCalendar();
    this.loadAppointments();
  }

  goToToday(): void {
    this.currentDate = new Date();
    this.generateCalendar();
    this.loadAppointments();
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}
